import {
  collection,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { generatePortalToken } from './portalToken';

export function normalizeCustomerEmail(email) {
  return String(email || '').trim().toLowerCase();
}

function parseEmailParts(email) {
  const e = normalizeCustomerEmail(email);
  if (!e) return { local: '', domain: '' };
  if (e.includes('@')) {
    const at = e.indexOf('@');
    return { local: e.slice(0, at), domain: e.slice(at + 1) };
  }
  return { local: '', domain: e };
}

function customerIdentityText(customer) {
  return `${customer.client || ''} ${customer.contact || ''}`.trim().toLowerCase();
}

function nameMatchesPrefer(customer, preferClient) {
  const prefer = String(preferClient || '').trim().toLowerCase();
  if (!prefer) return false;
  const identity = customerIdentityText(customer);
  if (!identity) return false;
  if (identity.includes(prefer) || prefer.includes(identity)) return true;
  return prefer.split(/\s+/).every((word) => word.length > 1 && identity.includes(word));
}

/**
 * Match exact email, billing email, domain-only typos, and same-domain Ben variants.
 */
export function customerMatchesDedupeTarget(customer, targetEmail, { preferClient = '' } = {}) {
  const target = normalizeCustomerEmail(targetEmail);
  if (!target) return false;

  const targetParts = parseEmailParts(target);
  const emails = [customer.email, customer.billingEmail].filter(Boolean);

  for (const field of emails) {
    const normalized = normalizeCustomerEmail(field);
    if (normalized === target) return true;

    const parts = parseEmailParts(field);
    if (targetParts.domain && parts.domain === targetParts.domain) {
      if (targetParts.local && parts.local === targetParts.local) return true;
      if (!parts.local && nameMatchesPrefer(customer, preferClient)) return true;
      if (!targetParts.local && parts.local === targetParts.local) return true;
    }
  }

  if (targetParts.domain && nameMatchesPrefer(customer, preferClient)) {
    return emails.some((field) => {
      const parts = parseEmailParts(field);
      return parts.domain === targetParts.domain || normalizeCustomerEmail(field) === targetParts.domain;
    });
  }

  return false;
}

export async function findCustomersByEmail(db, email, options = {}) {
  const snap = await getDocs(collection(db, 'customers'));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((c) => customerMatchesDedupeTarget(c, email, options));
}

export async function countOrdersForCustomer(db, customerId) {
  const snap = await getDocs(query(collection(db, 'orders'), where('customerId', '==', customerId)));
  return snap.size;
}

function sortCustomersForWinner(customers, preferClient) {
  const prefer = String(preferClient || '').trim().toLowerCase();
  return [...customers].sort((a, b) => {
    if (b.orderCount !== a.orderCount) return b.orderCount - a.orderCount;

    if (prefer) {
      const aMatch = nameMatchesPrefer(a, prefer) || String(a.client || '').toLowerCase().includes(prefer);
      const bMatch = nameMatchesPrefer(b, prefer) || String(b.client || '').toLowerCase().includes(prefer);
      if (aMatch && !bMatch) return -1;
      if (bMatch && !aMatch) return 1;
    }

    const aClientLen = String(a.client || '').length;
    const bClientLen = String(b.client || '').length;
    if (bClientLen !== aClientLen) return bClientLen - aClientLen;

    return String(a.client || '').localeCompare(String(b.client || ''));
  });
}

export async function analyzeDuplicateCustomers(db, email, { preferClient = '' } = {}) {
  const options = { preferClient };
  const matches = await findCustomersByEmail(db, email, options);
  const withCounts = await Promise.all(
    matches.map(async (customer) => ({
      ...customer,
      orderCount: await countOrdersForCustomer(db, customer.id)
    }))
  );

  const sorted = sortCustomersForWinner(withCounts, preferClient);

  return {
    matches: withCounts,
    winner: sorted[0] || null,
    losers: sorted.slice(1)
  };
}

async function reassignOrders(db, fromId, toId, toName) {
  const snap = await getDocs(query(collection(db, 'orders'), where('customerId', '==', fromId)));
  if (snap.empty) return 0;

  const batch = writeBatch(db);
  let count = 0;
  snap.docs.forEach((orderDoc) => {
    batch.update(orderDoc.ref, {
      customerId: toId,
      customerName: toName,
      updatedAt: serverTimestamp()
    });
    count += 1;
  });
  await batch.commit();
  return count;
}

async function reassignDesignMockups(db, fromId, toId) {
  const snap = await getDocs(
    query(collection(db, 'customerDesignMockups'), where('customerId', '==', fromId))
  );
  if (snap.empty) return 0;

  let count = 0;
  for (const mockupDoc of snap.docs) {
    await updateDoc(mockupDoc.ref, {
      customerId: toId,
      updatedAt: serverTimestamp()
    });
    count += 1;
  }
  return count;
}

async function normalizeWinnerRecord(db, winner, { canonicalEmail, preferClient }) {
  const updates = { updatedAt: serverTimestamp() };
  const email = normalizeCustomerEmail(canonicalEmail);
  if (email) updates.email = email;
  if (preferClient) {
    updates.client = preferClient;
    if (!winner.contact) updates.contact = preferClient;
  }
  if (!winner.portalToken) {
    updates.portalToken = generatePortalToken();
    updates.portalTokenCreatedAt = serverTimestamp();
    updates.portalEnabled = winner.portalEnabled !== false;
  }
  if (Object.keys(updates).length > 1) {
    await updateDoc(doc(db, 'customers', winner.id), updates);
    return { ...winner, ...updates };
  }
  return winner;
}

/**
 * Merge duplicate customer docs into the winner (most orders).
 */
export async function mergeDuplicateCustomers(db, email, { preferClient = '' } = {}) {
  const { winner, losers } = await analyzeDuplicateCustomers(db, email, { preferClient });

  if (!winner) {
    throw new Error('No customers found for that email.');
  }
  if (losers.length === 0) {
    const normalized = await normalizeWinnerRecord(db, winner, {
      canonicalEmail: email,
      preferClient
    });
    return { winner: normalized, ordersMoved: 0, mockupsMoved: 0, deleted: [] };
  }

  const displayName = preferClient || winner.client || winner.contact || 'Customer';

  let ordersMoved = 0;
  let mockupsMoved = 0;
  const deleted = [];

  for (const loser of losers) {
    ordersMoved += await reassignOrders(db, loser.id, winner.id, displayName);
    mockupsMoved += await reassignDesignMockups(db, loser.id, winner.id);
    await deleteDoc(doc(db, 'customers', loser.id));
    deleted.push({ id: loser.id, client: loser.client, email: loser.email, orderCount: loser.orderCount });
  }

  const normalizedWinner = await normalizeWinnerRecord(db, winner, {
    canonicalEmail: email,
    preferClient: preferClient || winner.client
  });

  return {
    winner: normalizedWinner,
    ordersMoved,
    mockupsMoved,
    deleted
  };
}
